import React, { useState, useEffect, useCallback } from 'react';
import axios from '../utils/api';
import { redirectToPortalLogin } from '../utils/portalLogin';
import GenerationPreviewButton from '../components/GenerationPreviewButton';
import './AdminGenerations.css';

interface Generation {
  id: number;
  generation_id: string;
  template_type: string;
  jurisdiction: string;
  site_type: string;
  language: string;
  status: string;
  created_at: string;
  archive_path?: string;
  user_email: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface QueueGeneration {
  generationId: string;
  jobId: string;
  userId: number | null;
  userEmail: string | null;
  htmlTemplate: string | null;
  state: string;
  queuedAt: string | null;
  progress: {
    status: string;
    percentage: number;
    stepName: string;
    updatedAt: string;
  } | null;
}

interface QueueStatus {
  queueSize: number;
  counts: {
    waiting: number;
    active: number;
    delayed: number;
    failed: number;
    completed: number;
  };
  globalActiveCount: number;
}

const AdminGenerations: React.FC = () => {
  const [generations, setGenerations] = useState<Generation[]>([]);
  const [users, setUsers] = useState<{id: number, email: string}[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<{id: number, email: string} | null>(null);
  const [pageSize, setPageSize] = useState(20);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<'top' | 'bottom'>('bottom');
  const [queueGenerations, setQueueGenerations] = useState<QueueGeneration[]>([]);
  const [queueStatus, setQueueStatus] = useState<QueueStatus | null>(null);
  const [queueLoading, setQueueLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);



  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.action-menu') && !target.closest('.action-trigger')) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Добавляем обработчик для закрытия меню при клике на Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const checkAuth = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      redirectToPortalLogin();
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get('/api/admin/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.map((user: any) => ({ id: user.id, email: user.email })));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  const fetchQueue = useCallback(async () => {
    try {
      setQueueLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get('/api/admin/queue/active', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQueueGenerations(response.data?.generations || []);
      setQueueStatus(response.data?.queueStatus || null);
    } catch (err) {
      console.error('Error fetching queue:', err);
    } finally {
      setQueueLoading(false);
    }
  }, []);

  const fetchGenerations = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Необходимо войти в систему');
        setLoading(false);
        return;
      }
      
      let endpoint = '/api/admin/generations';
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pageSize.toString(),
        sortBy,
        sortOrder,
        ...(searchQuery && { search: searchQuery })
      });
      
      if (selectedUser) {
        endpoint = `/api/admin/users/${selectedUser.id}/generations`;
      }
      
      const response = await axios.get(`${endpoint}?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.generations) {
        setGenerations(response.data.generations);
        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            total: response.data.pagination.total || response.data.generations.length,
            pages: response.data.pagination.pages || Math.ceil((response.data.pagination.total || response.data.generations.length) / pageSize)
          }));
        } else {
          setPagination(prev => ({
            ...prev,
            total: response.data.generations.length,
            pages: Math.ceil(response.data.generations.length / pageSize)
          }));
        }
      } else if (Array.isArray(response.data)) {
        setGenerations(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.length,
          pages: Math.ceil(response.data.length / pageSize)
        }));
      } else {
        console.error('Unexpected API response structure:', response.data);
        setError('Неожиданная структура ответа API');
      }
    } catch (error: any) {
      console.error('Error fetching generations:', error);
      setError(error.response?.data?.message || 'Ошибка при загрузке генераций');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pageSize, sortBy, sortOrder, searchQuery, selectedUser]);

  const handleCancelGeneration = useCallback(async (generationId: string) => {
    if (!window.confirm(`Отменить генерацию ${generationId}? Это освободит слот в очереди.`)) {
      return;
    }
    try {
      setCancellingId(generationId);
      const token = localStorage.getItem('token');
      await axios.post(`/api/admin/queue/cancel/${generationId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchQueue();
      fetchGenerations();
    } catch (err) {
      console.error('Error cancelling generation:', err);
      alert('Ошибка при отмене генерации');
    } finally {
      setCancellingId(null);
    }
  }, [fetchQueue, fetchGenerations]);

  const handleForceClean = useCallback(async () => {
    if (!window.confirm('Принудительно очистить очередь? Все зависшие/отменённые задачи будут удалены, счётчики сброшены.')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/admin/queue/force-clean', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchQueue();
      fetchGenerations();
    } catch (err) {
      console.error('Error force cleaning queue:', err);
      alert('Ошибка при очистке очереди');
    }
  }, [fetchQueue, fetchGenerations]);

  useEffect(() => {
    checkAuth();
    fetchUsers();
    fetchQueue();
  }, [checkAuth, fetchUsers, fetchQueue]);

  useEffect(() => {
    fetchGenerations();
  }, [fetchGenerations]);

  // Автообновление очереди каждые 10 секунд
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let stopped = false;
    const poll = () => {
      if (stopped) return;
      fetchQueue().finally(() => {
        if (!stopped) {
          timeoutId = setTimeout(poll, 10000);
        }
      });
    };
    timeoutId = setTimeout(poll, 10000);
    return () => {
      stopped = true;
      clearTimeout(timeoutId);
    };
  }, [fetchQueue]);

  const isStaleGeneration = (gen: QueueGeneration): boolean => {
    const ts = gen.progress?.updatedAt || gen.queuedAt;
    if (!ts) return false;
    return Date.now() - new Date(ts).getTime() > 15 * 60 * 1000;
  };

  const formatTimeSince = (ts: string | null): string => {
    if (!ts) return '—';
    const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (diff < 60) return `${diff} сек назад`;
    if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`;
    return `${Math.floor(diff / 3600)} ч назад`;
  };

  const getQueueStatePill = (state: string) => {
    if (state === 'active') return 'u2-pill is-warn';
    if (state === 'waiting' || state === 'delayed') return 'u2-pill';
    return 'u2-pill';
  };

  const getQueueStateText = (state: string) => {
    switch (state) {
      case 'active': return 'Выполняется';
      case 'waiting': return 'Ожидает';
      case 'delayed': return 'Отложена';
      default: return state;
    }
  };

  const getStatusPillClass = (status: string) => {
    if (status === 'completed') return 'u2-pill is-ok';
    if (status === 'failed' || status === 'cancelled') return 'u2-pill is-bad';
    if (status === 'pending' || status === 'processing') return 'u2-pill is-warn';
    return 'u2-pill';
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Завершена';
      case 'pending': return 'В процессе';
      case 'failed': return 'Ошибка';
      case 'processing': return 'Обработка';
      case 'cancelled': return 'Отменена';
      default: return status;
    }
  };

  const handleDownload = async (generationId: number) => {
    try {
      const response = await axios.get(`/api/admin/generations/${generationId}/download`, {
        responseType: 'blob'
      });
      
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `generation-${generationId}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading generation:', error);
      alert('Ошибка при скачивании файла');
    }
  };

  const handleDelete = async (generationId: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту генерацию?')) {
      return;
    }
    
    try {
      await axios.delete(`/api/admin/generations/${generationId}`);
      
      setGenerations(prev => prev.filter(g => g.id !== generationId));
      setPagination(prev => ({ ...prev, total: prev.total - 1 }));
      alert('Генерация успешно удалена');
    } catch (error) {
      console.error('Error deleting generation:', error);
      alert('Ошибка при удалении генерации');
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(search);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  if (loading) {
    return (
      <section className="u2-card is-gradient">
        <div className="u2-cardInner">
          <div className="u2-strong">Загрузка генераций…</div>
          <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>
            Пожалуйста, подождите.
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="u2-card is-gradient">
        <div className="u2-cardInner">
          <div className="u2-cardHeader" style={{ marginBottom: 0 }}>
            <div>
              <h2 className="u2-title">Ошибка</h2>
              <p className="u2-subtitle">{error}</p>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <button type="button" className="u2-btn is-primary" onClick={fetchGenerations}>
              Попробовать снова
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div className="u2-grid admin-generations">
      <section className="u2-card is-gradient">
        <div className="u2-cardInner">
          <div className="u2-cardHeader" style={{ marginBottom: selectedUser ? 10 : 0 }}>
            <div style={{ minWidth: 0 }}>
              <h1 className="u2-title">
                {selectedUser ? `Генерации пользователя ${selectedUser.email}` : 'Все генерации'}
              </h1>
              <p className="u2-subtitle">
                {selectedUser ? `Просмотр всех генераций пользователя ${selectedUser.email}` : 'Просмотр всех генераций пользователей'}
              </p>
            </div>
            <span className="u2-badge">Записей: {pagination.total}</span>
          </div>

          {selectedUser && (
            <button
              type="button"
              className="u2-btn is-ghost"
              onClick={() => {
                setSelectedUser(null);
                window.history.pushState({}, '', '/admin-generations');
              }}
            >
              Назад ко всем генерациям
            </button>
          )}
        </div>
      </section>

      {/* Секция управления очередью */}
      <section className="u2-card is-gradient admin-generations__queueSection">
        <div className="u2-cardInner">
          <div className="u2-cardHeader" style={{ marginBottom: 12 }}>
            <div>
              <h2 className="u2-title" style={{ fontSize: 16 }}>
                Очередь генераций
              </h2>
              <p className="u2-subtitle">
                Активные и ожидающие генерации. Обновляется автоматически.
              </p>
            </div>
            <div className="u2-row" style={{ gap: 8 }}>
              {queueStatus && (
                <div className="admin-generations__queueStats">
                  <div className="admin-generations__queueStat">
                    <span className="admin-generations__queueStatValue">{queueStatus.globalActiveCount}</span>
                    <span className="admin-generations__queueStatLabel">Активных</span>
                  </div>
                  <div className="admin-generations__queueStat">
                    <span className="admin-generations__queueStatValue">{queueStatus.counts.waiting + queueStatus.counts.delayed}</span>
                    <span className="admin-generations__queueStatLabel">В очереди</span>
                  </div>
                </div>
              )}
              <button
                type="button"
                className="u2-btn is-ghost"
                onClick={fetchQueue}
                disabled={queueLoading}
                style={{ fontSize: 12 }}
              >
                {queueLoading ? 'Обновляю…' : 'Обновить'}
              </button>
              <button
                type="button"
                className="u2-btn is-danger"
                onClick={handleForceClean}
                style={{ fontSize: 12, padding: '6px 12px' }}
              >
                Очистить
              </button>
            </div>
          </div>

          {queueGenerations.length === 0 ? (
            <div className="u2-muted" style={{ fontSize: 13 }}>
              Очередь пуста — нет активных или ожидающих генераций.
            </div>
          ) : (
            <div className="admin-generations__queueGrid">
              {queueGenerations.map((gen) => {
                const stale = isStaleGeneration(gen);
                return (
                  <div
                    key={gen.generationId}
                    className={`admin-generations__queueItem ${gen.state === 'active' ? 'is-active' : ''} ${stale ? 'is-stale' : ''}`}
                  >
                    <div className="admin-generations__queueInfo">
                      <div className="admin-generations__queueId">{gen.generationId}</div>
                      <div className="admin-generations__queueMeta">
                        <span className={getQueueStatePill(gen.state)}>{getQueueStateText(gen.state)}</span>
                        {gen.userEmail && <span>{gen.userEmail}</span>}
                        {gen.htmlTemplate && <span>{gen.htmlTemplate}</span>}
                      </div>
                      <div className="admin-generations__queueMeta">
                        {gen.progress && (
                          <>
                            <span>{gen.progress.stepName} ({gen.progress.percentage}%)</span>
                            <span>Обновлено: {formatTimeSince(gen.progress.updatedAt)}</span>
                          </>
                        )}
                        {!gen.progress && gen.queuedAt && (
                          <span>В очереди: {formatTimeSince(gen.queuedAt)}</span>
                        )}
                        {stale && (
                          <span style={{ color: 'hsl(355 85% 60%)', fontWeight: 600 }}>
                            Зависла (&gt;15 мин)
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="u2-btn is-danger admin-generations__cancelBtn"
                      style={{ fontSize: 12, padding: '6px 12px' }}
                      disabled={cancellingId === gen.generationId}
                      onClick={() => handleCancelGeneration(gen.generationId)}
                    >
                      {cancellingId === gen.generationId ? 'Отмена…' : 'Отменить'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="u2-card is-soft admin-generations__controls" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
        <div className="u2-cardInner">
          <div className="u2-row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <form onSubmit={handleSearch} className="u2-row" style={{ gap: 10, flexWrap: 'wrap', flex: 1, minWidth: 320 }}>
              <input
                type="text"
                placeholder="Поиск по email или ID генерации…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="u2-input"
                style={{ minWidth: 280, maxWidth: 520 }}
              />
              <button type="submit" className="u2-btn is-primary">
                Найти
              </button>
              {searchQuery && (
                <button
                  type="button"
                  className="u2-btn is-ghost"
                  onClick={() => {
                    setSearch('');
                    setSearchQuery('');
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                >
                  Очистить
                </button>
              )}
            </form>

            <div className="u2-row" style={{ gap: 10, alignItems: 'center' }}>
              <span className="u2-muted" style={{ fontSize: 12 }}>
                Показывать по
              </span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                className="u2-select"
                style={{ width: 120 }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      <section className="u2-card is-gradient">
        <div className="u2-cardInner">
          {generations.length === 0 ? (
            <div className="u2-muted" style={{ fontSize: 13 }}>
              Нет генераций.
            </div>
          ) : (
            <div className="u2-tableWrap admin-generations__tableWrap">
              <table className="u2-table">
                <thead>
                  <tr>
                    <th className="admin-generations__sortable" onClick={() => handleSort('generation_id')}>
                      ID
                    </th>
                    <th className="admin-generations__sortable" onClick={() => handleSort('user_email')}>
                      Пользователь
                    </th>
                    <th className="admin-generations__sortable" onClick={() => handleSort('template_type')}>
                      Тип
                    </th>
                    <th className="admin-generations__sortable" onClick={() => handleSort('jurisdiction')}>
                      Юрисдикция
                    </th>
                    <th className="admin-generations__sortable" onClick={() => handleSort('site_type')}>
                      Тип сайта
                    </th>
                    <th className="admin-generations__sortable" onClick={() => handleSort('language')}>
                      Язык
                    </th>
                    <th className="admin-generations__sortable" onClick={() => handleSort('status')}>
                      Статус
                    </th>
                    <th className="admin-generations__sortable" onClick={() => handleSort('created_at')}>
                      Дата
                    </th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {generations.map((generation) => (
                    <tr key={generation.id}>
                      <td>
                        <span className="admin-generations__mono">{generation.generation_id}</span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="admin-generations__userBtn"
                          onClick={() => {
                            const user = users.find((u) => u.email === generation.user_email);
                            if (user) {
                              setSelectedUser({ id: user.id, email: generation.user_email });
                              setPagination((prev) => ({ ...prev, page: 1 }));
                            }
                          }}
                          title="Показать генерации пользователя"
                        >
                          {generation.user_email}
                        </button>
                      </td>
                      <td>{generation.template_type}</td>
                      <td>{generation.jurisdiction}</td>
                      <td>{generation.site_type === 'single' ? 'Одностраничный' : 'Многостраничный'}</td>
                      <td>{generation.language}</td>
                      <td>
                        <span className={getStatusPillClass(generation.status)}>{getStatusText(generation.status)}</span>
                      </td>
                      <td>
                        {new Date(generation.created_at).toLocaleDateString('ru-RU', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="admin-generations__actions">
                        <div className="u2-toolbarGroup" style={{ flexWrap: 'nowrap' }}>
                        {generation.status === 'completed' && generation.archive_path && (
                          <GenerationPreviewButton generationId={generation.generation_id} />
                        )}
                        <div className="action-container">
                          <button
                            type="button"
                            className="u2-iconBtn action-trigger"
                            onClick={(e) => {
                              e.stopPropagation();
                              const button = e.currentTarget;
                              const rect = button.getBoundingClientRect();
                              const windowHeight = window.innerHeight;
                              const menuHeight = 150;

                              if (rect.bottom + menuHeight > windowHeight - 50) {
                                setMenuPosition('top');
                              } else {
                                setMenuPosition('bottom');
                              }

                              setOpenMenuId(openMenuId === generation.id ? null : generation.id);
                            }}
                            title="Действия"
                            aria-label="Действия"
                          >
                            <svg className="u2-ico" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <circle cx="12" cy="5" r="2" />
                              <circle cx="12" cy="12" r="2" />
                              <circle cx="12" cy="19" r="2" />
                            </svg>
                          </button>

                          {openMenuId === generation.id && (
                            <div
                              className={`action-menu ${menuPosition === 'top' ? 'menu-top' : 'menu-bottom'}`}
                              style={{ position: 'absolute', zIndex: 10000, minWidth: '200px' }}
                            >
                              {generation.status === 'completed' && generation.archive_path && (
                                <button
                                  type="button"
                                  className="action-item"
                                  onClick={() => {
                                    handleDownload(generation.id);
                                    setOpenMenuId(null);
                                  }}
                                >
                                  Скачать
                                </button>
                              )}
                              <button
                                type="button"
                                className="action-item is-danger"
                                onClick={() => {
                                  handleDelete(generation.id);
                                  setOpenMenuId(null);
                                }}
                              >
                                Удалить
                              </button>
                            </div>
                          )}
                        </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {pagination.pages > 1 && (
        <section className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
          <div className="u2-cardInner">
            <div className="u2-row" style={{ justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button
                className="u2-btn is-secondary"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                type="button"
              >
                Назад
              </button>
              <span className="u2-badge">
                Страница {pagination.page} из {pagination.pages} (всего: {pagination.total})
              </span>
              <button
                className="u2-btn is-secondary"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                type="button"
              >
                Вперёд
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default AdminGenerations;
